/**
 * Money parsing & formatting helpers — single source of truth for every
 * monetary value displayed by Hérita on the web. Designed to be byte-
 * for-byte aligned with what the PDF export renders for the same field,
 * so that the web UI and the PDF NEVER show different amounts for the
 * same source data.
 *
 * Conventions
 * -----------
 *  - All monetary amounts coming from the backend are STRINGS, e.g.
 *    "330 725,20 €", "1 250 000 €", "150 000,00 EUR" or "Non identifié".
 *  - We never parse-and-recompose lossily. Either we can extract a clean
 *    JS Number, in which case we re-format it from that number; or we
 *    fall back to the raw string verbatim. We never display a substring.
 *
 * Public API
 * ----------
 *  - parseFrenchMoney(raw)  → number | null
 *  - formatFrenchMoney(n, opts) → string
 *  - displayAmount(raw)     → string | null  (preferred for UI)
 *  - isMeaningfulAmount(raw) → boolean
 *  - isAnomalouslyLow(value, reference, ratio?) → boolean
 */

/** All Unicode whitespace variants that may appear in formatted FR money
 *  (NBSP, narrow NBSP, thin space, figure space, hair space, regular). */
const WS_RE = /[\s\u00A0\u202F\u2009\u2007\u200A]+/g;

/** Sentinel strings the LLM is allowed to return when no amount exists. */
const SENTINEL_RE = /^(non\s+identif|n[/]a|non\s+communiqu|inconnu)/i;

/**
 * Parse a French-formatted monetary string into a finite Number.
 *
 *   "330 725,20 €"       → 330725.20
 *   "1 250 000 €"        → 1250000
 *   "150 000,00 EUR"     → 150000
 *   "1.250.000,50 €"     → 1250000.50   (dots as thousand separators)
 *   "12 €"               → 12
 *   "Non identifié"      → null
 *   ""                   → null
 *
 * Returns null if no numeric value can be extracted (sentinel or noise).
 */
export const parseFrenchMoney = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (!s || SENTINEL_RE.test(s)) return null;

  // Normalise every whitespace variant to a single space.
  const normalised = s.replace(WS_RE, ' ');

  // Capture the longest contiguous numeric block (digits, dots, spaces,
  // commas) that ends just before "€", "EUR" or "euros". We REQUIRE a
  // currency marker — otherwise we would mis-parse phrases like
  // "Article 4" or "environ 4 millions". Callers that have non-currency
  // numeric data should pass it through formatFrenchMoney directly.
  const withCurrency = normalised.match(
    /([0-9][0-9 .,]*[0-9]|[0-9])\s*(?:€|EUR|euros?)/i,
  );
  if (!withCurrency) return null;
  const numericBlock = withCurrency[1];

  // French convention: comma = decimal separator, space/dot = thousands.
  // Pick the LAST comma (or last dot when there is no comma at all but a
  // single dot followed by 1-2 digits — that looks like a decimal point).
  const lastComma = numericBlock.lastIndexOf(',');
  let integerPart;
  let decimalPart = '';
  if (lastComma !== -1) {
    integerPart = numericBlock.slice(0, lastComma);
    decimalPart = numericBlock.slice(lastComma + 1);
  } else {
    // No comma — check if there is a single dot followed by ≤2 digits at
    // the end ; in that case treat it as a decimal separator. Otherwise
    // dots are thousand separators (FR + DE convention).
    const trailingDot = numericBlock.match(/^(.*)\.(\d{1,2})$/);
    if (trailingDot && !/\d{3}\.\d{3}/.test(numericBlock)) {
      integerPart = trailingDot[1];
      decimalPart = trailingDot[2];
    } else {
      integerPart = numericBlock;
    }
  }

  // Strip every thousand-separator character from the integer part.
  const digits = integerPart.replace(/[\s.]/g, '');
  if (!digits) return null;

  // Decimal part: keep only digits to ignore trailing currency noise.
  const decDigits = decimalPart.replace(/[^\d]/g, '');
  const composed = decDigits ? `${digits}.${decDigits}` : digits;
  const n = Number(composed);
  return Number.isFinite(n) ? n : null;
};

/**
 * Format a Number into a French monetary string with the same rules the
 * backend / PDF use. By default we keep cents only when the value has
 * non-zero decimals — so "1 250 000 €" stays unchanged while
 * "330 725,2" becomes "330 725,20 €".
 */
export const formatFrenchMoney = (n, opts = {}) => {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  const hasDecimals = Math.round(n) !== n;
  const fractionDigits = opts.alwaysCents ? 2 : hasDecimals ? 2 : 0;
  // Intl produces NBSP between thousand groups in fr-FR which is what we
  // want (visually crisp, identical to what the LLM/PDF use).
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n)} €`;
};

/**
 * Decide what to display in the UI for a given raw amount string.
 *
 *  - If the raw string can be cleanly parsed into a Number, return the
 *    canonical re-formatted version (preserves decimals correctly).
 *  - Otherwise return the raw string trimmed of whitespace if it looks
 *    like a meaningful monetary chunk; null on sentinel or junk.
 *
 * IMPORTANT: this function NEVER returns a substring of the input. It
 * either re-formats from a parsed Number or returns the input verbatim.
 */
export const displayAmount = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (!s || SENTINEL_RE.test(s)) return null;
  const n = parseFrenchMoney(s);
  if (n !== null) return formatFrenchMoney(n);
  // Fallback: return the trimmed raw string verbatim.
  return s;
};

/** True when the raw string yields a strictly positive parsed number. */
export const isMeaningfulAmount = (raw) => {
  const n = parseFrenchMoney(raw);
  return n !== null && n > 0;
};

/**
 * Anti-anomaly guard. Returns true when `value` is suspiciously low
 * compared to `reference` — useful to suppress hero amount cards whose
 * value was clearly mis-extracted (e.g. only the decimal slice "20 €"
 * surviving from "330 725,20 €").
 *
 * Default heuristic: anomaly if value / reference < 1 % AND reference
 * is itself meaningful (> 10 000 €). We only flag drastic mismatches.
 */
export const isAnomalouslyLow = (value, reference, ratio = 0.01) => {
  const v = parseFrenchMoney(value);
  const r = parseFrenchMoney(reference);
  if (v === null || r === null) return false;
  if (r < 10000) return false;
  if (v <= 0) return false;
  return v / r < ratio;
};
